/**
 * Conflict Prevention Engine V2
 * Detects and resolves logical contradictions in cinematic settings.
 * Includes text-vs-dropdown cross-validation and severity scoring.
 */

import { CanvasElement, GlobalSettings } from './engineTypes';

export interface Conflict {
  id: string;
  severity: 'info' | 'warning' | 'error';
  message: string;
  autoFix?: { key: string; value: string | number | boolean; target: 'global' | 'element'; elementId?: string };
}

interface ConflictRule {
  id: string;
  check: (elements: CanvasElement[], gs: GlobalSettings, scene?: string) => Conflict | null;
}

// ─── Text-vs-Dropdown cross-validation patterns ───

interface TextDropdownPattern {
  textPattern: RegExp;
  conflictsWith: { type: 'element'; elType: string; propKey: string; propValues: string[] } | { type: 'global'; key: string; values: (string | number | boolean)[] };
  message: string;
  severity: Conflict['severity'];
  autoFix?: Conflict['autoFix'];
}

const TEXT_DROPDOWN_RULES: TextDropdownPattern[] = [
  // Text says night but lighting dropdown is daylight/natural high
  {
    textPattern: /\b(night|ليل|بالليل|midnight|dark\s*sky|نجوم|مسا|ظلام)\b/i,
    conflictsWith: { type: 'element', elType: 'light', propKey: 'lightStyle', propValues: ['natural'] },
    message: 'Scene text describes night but lighting is set to Natural Daylight — auto-switched to Moonlight.',
    severity: 'error',
  },
  // Text says tight portrait but lens is ultra-wide
  {
    textPattern: /\b(close.?up|portrait|وجه|قريب|tight\s*shot|facial|ملامح)\b/i,
    conflictsWith: { type: 'element', elType: 'camera', propKey: 'lens', propValues: ['14mm', '18mm', '24mm'] },
    message: 'Scene describes close portrait but lens is ultra-wide — adjusted to 85mm portrait lens.',
    severity: 'error',
  },
  // Text says wide landscape but lens is telephoto
  {
    textPattern: /\b(wide|landscape|panorama|بانوراما|واسع|aerial|جوي|establishing)\b/i,
    conflictsWith: { type: 'element', elType: 'camera', propKey: 'lens', propValues: ['85mm', '100mm', '135mm', '200mm'] },
    message: 'Scene describes wide landscape but lens is telephoto — adjusted to 24mm wide angle.',
    severity: 'warning',
  },
  // Text says bright sunny but render style is dark/noir
  {
    textPattern: /\b(sunny|مشمس|bright|daylight|نهار|صافي|clear\s*sky)\b/i,
    conflictsWith: { type: 'global', key: 'renderStyle', values: ['noir', 'neo_noir', 'moody_noir'] },
    message: 'Bright sunny scene conflicts with Noir render style — adjusted render to Photorealistic.',
    severity: 'warning',
  },
  // Text says horror/dark but color grading is teal & orange cheerful
  {
    textPattern: /\b(horror|رعب|مرعب|dark|مظلم|creepy|مخيف|sinister)\b/i,
    conflictsWith: { type: 'global', key: 'tealOrangePreset', values: [true] },
    message: 'Horror mood conflicts with cheerful Teal & Orange grading — disabled for coherence.',
    severity: 'warning',
  },
];

function checkTextDropdownConflicts(
  elements: CanvasElement[],
  gs: GlobalSettings,
  scene?: string
): Conflict[] {
  if (!scene?.trim()) return [];
  const conflicts: Conflict[] = [];

  for (const rule of TEXT_DROPDOWN_RULES) {
    if (!rule.textPattern.test(scene)) continue;

    const { conflictsWith } = rule;

    if (conflictsWith.type === 'element') {
      const el = elements.find(
        e => e.type === conflictsWith.elType && conflictsWith.propValues.includes(e.properties[conflictsWith.propKey] || '')
      );
      if (el) {
        // Auto-fix based on the conflict
        let autoFix: Conflict['autoFix'] | undefined;
        if (conflictsWith.propKey === 'lightStyle' && conflictsWith.propValues.includes('natural')) {
          autoFix = { key: 'lightStyle', value: 'moonlight', target: 'element', elementId: el.id };
        } else if (conflictsWith.propKey === 'lens' && ['14mm', '18mm', '24mm'].some(v => conflictsWith.propValues.includes(v))) {
          autoFix = { key: 'lens', value: '85mm', target: 'element', elementId: el.id };
        } else if (conflictsWith.propKey === 'lens' && ['85mm', '100mm', '135mm', '200mm'].some(v => conflictsWith.propValues.includes(v))) {
          autoFix = { key: 'lens', value: '24mm', target: 'element', elementId: el.id };
        }
        conflicts.push({ id: `txt_${rule.textPattern.source.slice(0, 12)}`, severity: rule.severity, message: rule.message, autoFix });
      }
    } else if (conflictsWith.type === 'global') {
      const currentVal = (gs as any)[conflictsWith.key];
      if (conflictsWith.values.includes(currentVal)) {
        let autoFix: Conflict['autoFix'] | undefined;
        if (conflictsWith.key === 'renderStyle') {
          autoFix = { key: 'renderStyle', value: 'photorealistic', target: 'global' };
        } else if (conflictsWith.key === 'tealOrangePreset') {
          autoFix = { key: 'tealOrangePreset', value: false, target: 'global' };
        }
        conflicts.push({ id: `txt_g_${conflictsWith.key}`, severity: rule.severity, message: rule.message, autoFix });
      }
    }
  }
  return conflicts;
}

// ─── Original parameter-level conflict rules ───

const CONFLICT_RULES: ConflictRule[] = [
  {
    id: 'night_sun',
    check: (elements, _gs, scene) => {
      const isNight = scene && /\b(night|ليل|بالليل|midnight|dark sky|نجوم|stars)\b/i.test(scene);
      if (!isNight) return null;
      const harshSun = elements.find(e =>
        e.type === 'light' && e.properties.lightStyle === 'natural' && parseInt(e.properties.intensity || '50') > 70
      );
      if (!harshSun) return null;
      return {
        id: 'night_sun', severity: 'error',
        message: 'Night scene with intense natural sunlight — adjusted to moonlight.',
        autoFix: { key: 'lightStyle', value: 'moonlight', target: 'element', elementId: harshSun.id },
      };
    },
  },
  {
    id: 'portrait_aerial',
    check: (elements) => {
      const cam = elements.find(e => e.type === 'camera');
      if (!cam) return null;
      const isPortrait = parseInt(cam.properties.lens || '50') >= 85;
      const isAerial = cam.properties.perspective === 'birds_eye' || cam.properties.shotType === 'extreme_wide';
      if (isPortrait && isAerial) {
        return {
          id: 'portrait_aerial', severity: 'warning',
          message: 'Portrait lens (85mm+) conflicts with aerial/extreme wide — adjusted to 35mm.',
          autoFix: { key: 'lens', value: '35mm', target: 'element', elementId: cam.id },
        };
      }
      return null;
    },
  },
  {
    id: 'fog_sharp',
    check: (_el, gs) => {
      if (gs.fogHaze > 60 && gs.microDetail > 70) {
        return {
          id: 'fog_sharp', severity: 'warning',
          message: 'Heavy fog reduces micro-detail — reduced to match atmosphere.',
          autoFix: { key: 'microDetail', value: 40, target: 'global' },
        };
      }
      return null;
    },
  },
  {
    id: 'engine_dslr',
    check: (elements, gs) => {
      const isEngine = ['unreal_engine', 'aaa_game'].includes(gs.renderStyle);
      const hasDSLR = elements.some(e => e.type === 'camera' && e.properties.cameraType === 'dslr');
      if (isEngine && hasDSLR && gs.realCameraMode) {
        return {
          id: 'engine_dslr', severity: 'warning',
          message: 'Game engine render conflicts with DSLR mode — disabled camera simulation.',
          autoFix: { key: 'realCameraMode', value: false, target: 'global' },
        };
      }
      return null;
    },
  },
  {
    id: 'slowmo_longexp',
    check: (elements, gs) => {
      const cam = elements.find(e => e.type === 'camera');
      if (!cam) return null;
      if (cam.properties.cameraMovement === 'slow_motion' && gs.motionBlurGlobal > 60) {
        return {
          id: 'slowmo_longexp', severity: 'warning',
          message: 'Slow motion + heavy motion blur conflict — reduced blur.',
          autoFix: { key: 'motionBlurGlobal', value: 15, target: 'global' },
        };
      }
      return null;
    },
  },
  {
    id: 'grain_denoise',
    check: (_el, gs) => {
      if (gs.filmGrain && gs.noiseReduction > 70) {
        return {
          id: 'grain_denoise', severity: 'warning',
          message: 'Film grain + noise reduction cancel each other — reduced denoising.',
          autoFix: { key: 'noiseReduction', value: 20, target: 'global' },
        };
      }
      return null;
    },
  },
  {
    id: 'vintage_modern',
    check: (_el, gs) => {
      if (gs.vintageMode && gs.renderQuality === '8k' && gs.sharpness > 80) {
        return {
          id: 'vintage_modern', severity: 'warning',
          message: 'Vintage look conflicts with 8K sharpness — softened.',
          autoFix: { key: 'sharpness', value: 45, target: 'global' },
        };
      }
      return null;
    },
  },
  {
    id: 'silhouette_beauty',
    check: (elements, gs) => {
      if (elements.some(e => e.type === 'light' && e.properties.lightStyle === 'silhouette') && gs.skinSmoothing > 30) {
        return {
          id: 'silhouette_beauty', severity: 'warning',
          message: 'Silhouette makes skin retouch invisible — disabled beauty.',
          autoFix: { key: 'skinSmoothing', value: 0, target: 'global' },
        };
      }
      return null;
    },
  },
  {
    id: 'color_clash',
    check: (_el, gs) => {
      if (gs.tealOrangePreset && gs.vintageMode) {
        return {
          id: 'color_clash', severity: 'warning',
          message: 'Teal & Orange conflicts with Vintage — prioritizing teal & orange.',
          autoFix: { key: 'vintageMode', value: false, target: 'global' },
        };
      }
      return null;
    },
  },
  {
    id: 'ca_sharpness',
    check: (_el, gs) => {
      if (gs.chromaticAberration > 60 && gs.sharpness > 80) {
        return {
          id: 'ca_sharpness', severity: 'warning',
          message: 'Chromatic aberration undermines sharpness — balanced.',
          autoFix: { key: 'chromaticAberration', value: 30, target: 'global' },
        };
      }
      return null;
    },
  },
];

// ─── Public API ───

export function detectConflicts(
  elements: CanvasElement[],
  globalSettings: GlobalSettings,
  sceneDescription?: string
): Conflict[] {
  const conflicts: Conflict[] = [];

  // 1) Text-vs-Dropdown cross-validation
  const textConflicts = checkTextDropdownConflicts(elements, globalSettings, sceneDescription);
  conflicts.push(...textConflicts);

  // 2) Parameter-level rules (skip duplicates from text rules)
  const textIds = new Set(textConflicts.map(c => c.id));
  for (const rule of CONFLICT_RULES) {
    if (textIds.has(rule.id)) continue; // Already covered by text rule
    const result = rule.check(elements, globalSettings, sceneDescription);
    if (result) conflicts.push(result);
  }

  // Deduplicate by id
  const seen = new Set<string>();
  return conflicts.filter(c => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
}

/**
 * Compute whether generation should be locked.
 * Locked if any conflict has severity 'error'.
 */
export function isGenerationLocked(conflicts: Conflict[]): boolean {
  return conflicts.some(c => c.severity === 'error');
}

/**
 * Get overall severity score: 0 = clean, 1 = info only, 2 = warnings, 3 = errors (locked)
 */
export function getSeverityScore(conflicts: Conflict[]): number {
  if (conflicts.length === 0) return 0;
  if (conflicts.some(c => c.severity === 'error')) return 3;
  if (conflicts.some(c => c.severity === 'warning')) return 2;
  return 1;
}

/**
 * Apply auto-fixes and return corrected copies.
 * Does NOT mutate inputs.
 */
export function resolveConflicts(
  elements: CanvasElement[],
  globalSettings: GlobalSettings,
  conflicts: Conflict[]
): { elements: CanvasElement[]; globalSettings: GlobalSettings } {
  let gs = { ...globalSettings };
  let els = elements.map(e => ({ ...e, properties: { ...e.properties } }));

  for (const c of conflicts) {
    if (!c.autoFix) continue;
    const { key, value, target, elementId } = c.autoFix;
    if (target === 'global') {
      (gs as any)[key] = value;
    } else if (target === 'element' && elementId) {
      els = els.map(e =>
        e.id === elementId ? { ...e, properties: { ...e.properties, [key]: String(value) } } : e
      );
    }
  }

  return { elements: els, globalSettings: gs };
}
